import {
  ALL_FREQUENCIES,
  DAILY_FREQUENCIES,
  MONTHLY_AND_BELOW_FREQUENCIES,
  WEEKLY_AND_BELOW_FREQUENCIES,
} from "@/assets/constants/frequencies";
import { i18n } from "@/main.js";

// List of all performance statistics we can add in our different components.
export const PerformanceStatistic = Object.freeze({
  alpha: {
    id: "alpha",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "alpha",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.alpha"),
  },
  annualizedReturn: {
    id: "annualizedReturn",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "annualized-return",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.annualizedReturn"),
  },
  annualizedVolatility: {
    id: "annualizedVolatility",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "annualized-volatility",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.annualizedVolatility"),
  },
  annualizedVolatilityDaily: {
    id: "annualizedVolatilityDaily",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "annualized-volatility",
    requireBenchmark: false,
    allowedUpdateFrequencies: DAILY_FREQUENCIES,
    getName: () => i18n.t("perfStats.annualizedVolatilityDaily"),
  },
  beta: {
    id: "beta",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "beta",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.beta"),
  },
  correlation: {
    id: "correlation",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "correlation",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.correlation"),
  },
  cumulativeReturn: {
    id: "cumulativeReturn",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "cumulative-return",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.cumulativeReturn"),
  },
  informationRatio: {
    id: "informationRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "information-ratio",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.informationRatio"),
  },
  activeInformationRatio: {
    id: "activeInformationRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.activeInformationRatio"),
  },
  maximumDrawdown: {
    id: "maximumDrawdown",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "maximum-drawdown",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.maximumDrawdown"),
  },
  returnToVolatilityRatio: {
    id: "returnToVolatilityRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "return-to-volatility-ratio",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.returnToVolatilityRatio"),
  },
  sharpeRatio: {
    id: "sharpeRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "sharpe-ratio",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.sharpeRatio"),
  },
  sortinoRatio: {
    id: "sortinoRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "sortino-ratio",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.sortinoRatio"),
  },
  painRatio: {
    id: "painRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "pain-ratio",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.painRatio"),
  },
  // This statistic is not generally available as a selectable option in the UI and is only
  // calculated and displayed in the demo investment proposal section. Typically, we use the pain
  // ratio, but for the demo investment proposal, optimization using the pain index is possible, so we
  // display this statistic specifically in that context.
  painIndex: {
    id: "painIndex",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.painIndex"),
  },
  calmarRatio: {
    id: "calmarRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "calmar-ratio",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.calmarRatio"),
  },
  omegaRatio: {
    id: "omegaRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "omega-ratio",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.omegaRatio"),
  },
  minimum: {
    id: "minimum",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.minimum"),
  },
  maximum: {
    id: "maximum",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.maximum"),
  },
  skewness: {
    id: "skewness",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "skewness",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.skewness"),
  },
  kurtosis: {
    id: "kurtosis",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "kurtosis",
    requireBenchmark: false,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.kurtosis"),
  },
  trackingError: {
    id: "trackingError",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "tracking-error",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.trackingError"),
  },
  treynorRatio: {
    id: "treynorRatio",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}f`,
    knowledgeBaseTerm: "treynor-ratio",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.treynorRatio"),
  },
  m2RiskAdjustedPerfMonthly: {
    id: "m2RiskAdjustedPerfMonthly",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "m2-risk-adjusted-perf",
    requireBenchmark: true,
    allowedUpdateFrequencies: ALL_FREQUENCIES,
    getName: () => i18n.t("perfStats.m2RiskAdjustedPerfMonthly"),
  },
  averageMonthlyReturns: {
    id: "averageMonthlyReturns",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.averageMonthlyReturns"),
  },
  averagePositiveMonthlyReturns: {
    id: "averagePositiveMonthlyReturns",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.averagePositiveMonthlyReturns"),
  },
  averageNegativeMonthlyReturns: {
    id: "averageNegativeMonthlyReturns",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.averageNegativeMonthlyReturns"),
  },
  ratioOfPositiveMonthlyReturns: {
    id: "ratioOfPositiveMonthlyReturns",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.ratioOfPositiveMonthlyReturns"),
  },
  ratioOfNegativeMonthlyReturns: {
    id: "ratioOfNegativeMonthlyReturns",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.ratioOfNegativeMonthlyReturns"),
  },
  maxDaysUnderwater: {
    id: "maxDaysUnderwater",
    getFormat: () => ",.0f",
    allowedUpdateFrequencies: DAILY_FREQUENCIES,
    getName: () => i18n.t("perfStats.maxDaysUnderwater"),
  },
  maxMonthsUnderwater: {
    id: "maxMonthsUnderwater",
    getFormat: () => ",.0f",
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.maxMonthsUnderwater"),
  },
  maxPositiveMonthlyReturn: {
    id: "maxPositiveMonthlyReturn",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.maxPositiveMonthlyReturn"),
  },
  maxNegativeMonthlyReturn: {
    id: "maxNegativeMonthlyReturn",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.maxNegativeMonthlyReturn"),
  },
  maxConsecutivePositiveMonthlyReturns: {
    id: "maxConsecutivePositiveMonthlyReturns",
    getFormat: () => ",.0f",
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.maxConsecutivePositiveMonthlyReturns"),
  },
  maxConsecutiveNegativeMonthlyReturns: {
    id: "maxConsecutiveNegativeMonthlyReturns",
    getFormat: () => ",.0f",
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.maxConsecutiveNegativeMonthlyReturns"),
  },
  valueAtRiskOnePercentOneMonth: {
    id: "valueAtRiskOnePercentOneMonth",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "value-at-risk",
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.valueAtRiskOnePercentOneMonth"),
  },
  valueAtRiskFivePercentOneMonth: {
    id: "valueAtRiskFivePercentOneMonth",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "value-at-risk",
    requireBenchmark: false,
    allowedUpdateFrequencies: MONTHLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.valueAtRiskFivePercentOneMonth"),
  },
  valueAtRiskOnePercentTwoWeeks: {
    id: "valueAtRiskOnePercentTwoWeeks",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "value-at-risk",
    requireBenchmark: false,
    allowedUpdateFrequencies: WEEKLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.valueAtRiskOnePercentTwoWeeks"),
  },
  valueAtRiskFivePercentTwoWeeks: {
    id: "valueAtRiskFivePercentTwoWeeks",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "value-at-risk",
    requireBenchmark: false,
    allowedUpdateFrequencies: WEEKLY_AND_BELOW_FREQUENCIES,
    getName: () => i18n.t("perfStats.valueAtRiskFivePercentTwoWeeks"),
  },
  valueAtRiskOnePercentOneDay: {
    id: "valueAtRiskOnePercentOneDay",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "value-at-risk",
    requireBenchmark: false,
    allowedUpdateFrequencies: DAILY_FREQUENCIES,
    getName: () => i18n.t("perfStats.valueAtRiskOnePercentOneDay"),
  },
  valueAtRiskFivePercentOneDay: {
    id: "valueAtRiskFivePercentOneDay",
    getFormat: (numberOfDecimals) => `,.${numberOfDecimals}%`,
    knowledgeBaseTerm: "value-at-risk",
    requireBenchmark: false,
    allowedUpdateFrequencies: DAILY_FREQUENCIES,
    getName: () => i18n.t("perfStats.valueAtRiskFivePercentOneDay"),
  },
});

/**
 * Return true if the stat is allowed for the given product update frequency.
 * @param {string} statId - Stat id (e.g. "annualizedReturn").
 * @param {UpdateFrequency} updateFrequency - Product update frequency object.
 * @returns {boolean}
 */
export function isStatAllowedForFrequency(statId, updateFrequency) {
  const stat = Object.values(PerformanceStatistic).find((s) => s.id === statId);
  return stat.allowedUpdateFrequencies.map((freq) => freq.id).includes(updateFrequency.id);
}

/**
 * Return true if the stat can be requested/shown when no benchmark is available.
 * @param {string} statId - Stat id (e.g. "alpha").
 * @returns {boolean}
 */
export function isStatAllowedWithoutBenchmark(statId) {
  return !PerformanceStatistic[statId]?.requireBenchmark;
}

/**
 * Filter out stats that require a benchmark when none is available.
 * @param {string[]} statIds - Stat ids to filter.
 * @param {boolean} hasBenchmark - Whether a usable benchmark is available.
 * @returns {string[]}
 */
export function filterStatIdsForBenchmark(statIds, hasBenchmark) {
  if (hasBenchmark) {
    return statIds;
  }
  return statIds.filter(isStatAllowedWithoutBenchmark);
}

// Standard list of performance statistics that we want to show (with benchmark).
export const STANDARD_PERF_STATS_WITH_BENCHMARK = [
  PerformanceStatistic.cumulativeReturn,
  PerformanceStatistic.annualizedReturn,
  PerformanceStatistic.annualizedVolatility,
  PerformanceStatistic.maximumDrawdown,
  PerformanceStatistic.sharpeRatio,
  PerformanceStatistic.skewness,
  PerformanceStatistic.trackingError,
  PerformanceStatistic.informationRatio,
  PerformanceStatistic.alpha,
  PerformanceStatistic.beta,
];

// Standard list of performance statistics that we want to show (without benchmark).
export const STANDARD_PERF_STATS_WITHOUT_BENCHMARK = [
  PerformanceStatistic.cumulativeReturn,
  PerformanceStatistic.annualizedReturn,
  PerformanceStatistic.annualizedVolatility,
  PerformanceStatistic.maximumDrawdown,
  PerformanceStatistic.sharpeRatio,
  PerformanceStatistic.skewness,
];
